import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import MasterLayout from '../../components/layouts/masterlayout';
import ErrorMessage from '../../components/common/errormessage';
import SuccessMessage from '../../components/common/successmessage';
import InfoMessage from '../../components/common/infomessage';
import CustomerModal from '../clients/client_modal.js';
import ClientModal from '../common/modals/client_modal';
import MenuDropdown from '../common/dropdowns/dropdown_menu';
import OrderItems from './order_items';
import { getHeaders } from '../../lib/requests';
import GeneralButton from '../common/button_general';
import SimpleNumberInput from '../common/simple_number_input';
import Spinner from '../common/spinner';
import { fetchDelivery, fetchDeliveriesForCustomer } from '../../services/delivery_services.js';
import { fetchCustomer, fetchItemsFromDefaultOrder, fetchMostRecentScheduledOrder } from '../../services/order_services.js';
import ClientDeliveryDropdown from '../common/dropdowns/dropdown_client_deliveries.js';
import { UserContext } from '../../lib/usercontext';
import { updateCustomer } from '../../services/customer_services';
import { handlePdfFile } from '../../lib/utils';

function DefaultOrderModal({ isOpen, onClose, onSelect }) {
  if (!isOpen) return null;
  return (
    <div className="fixed z-50 inset-0 overflow-y-auto flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white p-4 rounded-md shadow-md">
        <h2 className="text-lg font-semibold mb-2">Which default order would you like to use?</h2>
        <div className="grid grid-cols-2 gap-2 mb-4">
          {[1, 2, 3, 4].map(num => (
            <button
              key={num}
              onClick={() => onSelect(num)}
              className="px-4 py-2 bg-blue-500 text-white rounded"
            >
              Default Order {num}
            </button>
          ))}
        </div>
        <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">
          Cancel
        </button>
      </div>
    </div>
  );
}

function OrderForm() {
  const itemsCache = useRef({});
  const CACHE_DURATION = 5 * 60 * 1000;
  const { orderId } = useParams();
  const { user } = useContext(UserContext);
  const role = user.userGroups ? user.userGroups[0] : 'Customer';
  const isAdmin = user.userGroups ? user.userGroups.includes('Admin') : false;
  let isCustomer = user.userGroups ? user.userGroups.includes('Customer') : false;
  if (isAdmin) isCustomer = false;
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isDefaultOrder, setIsDefaultOrder] = useState(false);
  const queryParams = new URLSearchParams(useLocation().search);
  const [selectedMenu, setSelectedMenu] = useState({ menu_id: null, name: '' });
  const [formData, setFormData] = useState({
    client_id: null,
    customer_id: null,
    facility_id: null,
    provider_id: null,
    order_group_id: null,
    items: [{ order_item_id: null, item_id: null, variation_id: null, quantity: 1, stock_status: null, create_date: null, modified_date: null }],
    delivery_date: null,
    scheduled_delivery_date: null,
    meal_count: 1,
    delivery_status: 'scheduled'
  });
  const quantityRefs = useRef([]);
  useEffect(() => {
    if (formData.items.length !== quantityRefs.current.length) {
      quantityRefs.current = formData.items.map((_, i) => quantityRefs.current[i] || null);
    }
  }, [formData.items]);
  const handleEnterPress = (index) => {
    if (quantityRefs.current[index + 1]) {
      quantityRefs.current[index + 1].focus();
      quantityRefs.current[index + 1].select();
    } else {
      console.log(`No input found at index ${index + 1}`);
    }
  };
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState({ client_id: formData.client_id, name: '' });
  const [selectedCustomer, setSelectedCustomer] = useState({ customer_id: formData.customer_id, first_name: '', last_name: '' });
  const [items, setItems] = useState([]);
  let isEditable = formData.delivery_status === 'scheduled' || formData.is_default_order;

  const fetchItems = async () => {
    try {
      const cacheKey = 'all';
      if (itemsCache.current[cacheKey] && Date.now() - itemsCache.current[cacheKey].timestamp < CACHE_DURATION) {
        setItems(itemsCache.current[cacheKey].data);
        return;
      }
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${apiUrl}/items?pageSize=1000`, {
        headers: getHeaders(apiToken),
        params: {},
      });
      itemsCache.current[cacheKey] = {
        data: response.data.data,
        timestamp: Date.now()
      };
      setItems(response.data.data);
    } catch (error) {
      console.error('Error fetching items', error);
    }
  };

  const fetchCustomers = async () => {
    setIsLoading(true);
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${apiUrl}/customers`, {
        headers: getHeaders(apiToken),
      });
      if (response.data.data.length > 0) {
        const firstCustomer = response.data.data[0];
        setSelectedCustomer({ 
          customer_id: firstCustomer.customer_id, 
          first_name: firstCustomer.first_name, 
          last_name: firstCustomer.last_name, 
          meal_count: firstCustomer.meal_count, 
          dietary_restriction_type: firstCustomer.dietary_restriction_type
        });
        setFormData(prevFormData => ({
          ...prevFormData,
          customer_id: firstCustomer.customer_id,
          meal_count: firstCustomer.meal_count,
          dietary_restriction_type: firstCustomer.dietary_restriction_type,
          menu_id: firstCustomer.default_menu_id
        }));
        if (location.pathname === '/orders/default') {
          navigate(`/orders/create?customer_id=${firstCustomer.customer_id}`);
        }
      }
    } catch (error) {
      console.error('Error fetching customers', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOrderData = async () => {
    if (orderId === 'default') {
      fetchCustomers();
      return;
    }
    const delivery_id = queryParams.get('delivery_id');
    const customer_id = queryParams.get('customer_id');
    if (delivery_id) {
      setIsLoading(true);
      let fetchedDelivery = await fetchDelivery(delivery_id);
      setSelectedClient({ client_id: fetchedDelivery.client_id, name: fetchedDelivery.client_name });

      if (fetchedDelivery.customer_id) {
        const customerData = await fetchCustomer(fetchedDelivery.customer_id);
        setSelectedCustomer({
          customer_id: customerData.customer_id,
          first_name: customerData.first_name,
          last_name:  customerData.last_name,
          meal_count: customerData.meal_count,
          dietary_restriction_type: customerData.dietary_restriction_type
        });
        setFormData(prev => ({
          ...prev,
          customer_id: customerData.customer_id,
          meal_count:  customerData.meal_count,
          dietary_restriction_type: customerData.dietary_restriction_type,
          menu_id:    customerData.default_menu_id
        }));
      }

      setFormData(prevFormData => ({
        ...prevFormData,
        delivery_date: fetchedDelivery.delivery_date ? new Date(fetchedDelivery.delivery_date).toISOString().split('T')[0] : '',
        scheduled_delivery_date: fetchedDelivery.scheduled_delivery_date ? new Date(fetchedDelivery.scheduled_delivery_date).toISOString().split('T')[0] : '',
        client_id: fetchedDelivery.client_id,
        delivery_id: delivery_id
      }));
      setIsLoading(false);        
    } else if (orderId) {
      setIsLoading(true);
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/orders/${orderId}`, { headers: getHeaders(apiToken) });
        const orderData = response.data.data[0];
        setSelectedClient({ client_id: orderData.client_id, name: orderData.client_name });
        setSelectedCustomer({ customer_id: orderData.customer_id, first_name: orderData.customer_first_name, last_name: orderData.customer_last_name });
        let delivery_date = orderData.delivery_date ? new Date(orderData.delivery_date).toISOString().split('T')[0] : '';
        let scheduled_delivery_date = orderData.scheduled_delivery_date ? new Date(orderData.scheduled_delivery_date).toISOString().split('T')[0] : '';
        isEditable = orderData.delivery_status === 'scheduled' || orderData.is_default_order;
        let meal_count = orderData.items.reduce((acc, item) => acc + item.quantity, 0);
        if (isEditable) {
          const customerData = await fetchCustomer(orderData.customer_id);
          meal_count = customerData.meal_count || meal_count;
        }
        setFormData(prevFormData => ({
          ...prevFormData,
          ...orderData,
          delivery_date,
          scheduled_delivery_date,
          delivery_status: orderData.delivery_status,
          meal_count
        }));
        setIsDefaultOrder(orderData.is_default_order);
        setShowError(false);
      } catch (error) {
        if (error.response.status === 404) {
          navigate('/orders?error=order-not-found');
          return;
        } else {
          setShowError(true);
          setErrorMessage('Failed to load order data');
          console.error('Error fetching order data:', error);
        }
      } finally {
        setIsLoading(false);
      }
    } else if (customer_id) {
      setIsLoading(true);
      try {
        const apiToken = localStorage.getItem('accessToken');
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await axios.get(`${apiUrl}/customers/${customer_id}`, { headers: getHeaders(apiToken) });
        const customerData = response.data.data;
        const defaultOrderNumber = queryParams.get('default_order') || '1';
        const defaultOrderField = `default_order_${defaultOrderNumber}_id`;
        const order_id = customerData[defaultOrderField];
        if (order_id) {
          const orderResponse = await axios.get(`${apiUrl}/orders/${order_id}`, { headers: getHeaders(apiToken) });
          const orderData = orderResponse.data.data[0];
          if (orderData.order_id) {
            navigate(`/orders/${order_id}`);
          }
        } else {
          const newOrderResponse = await axios.post(
            `${apiUrl}/orders`,
            { 
              customer_id: customer_id,
              meal_count: customerData.meal_count,
              menu_id: customerData.default_menu_id
            },
            { headers: getHeaders(apiToken) }
          );
          const orderIdResult = newOrderResponse.data.data.order_id;
          const newOrderId = typeof orderIdResult === 'object' && orderIdResult.order_id
            ? orderIdResult.order_id
            : orderIdResult;
          await axios.put(
            `${apiUrl}/customers/${customer_id}`,
            { [defaultOrderField]: newOrderId },
            { headers: getHeaders(apiToken) }
          );
          navigate(`/orders/${newOrderId}`);
        }
      } catch (error) {
        if (error.response.status === 404) {
          updateCustomer(customer_id, { default_order_1_id: null });
          navigate(`/clients/${customer_id}?error=default-order-not-found`);
        }
        console.error('Error managing default order:', error);
        setShowError(true);
        setErrorMessage('Failed to manage default order');
      } finally {
        setIsLoading(false);
      }
    } else {
      fetchCustomers();
      if (role === 'Customer') {
        setIsLoading(true);
        try {
          const openOrder = await fetchMostRecentScheduledOrder();
          if (openOrder) {
            navigate(`/orders/${openOrder.order_id}`);
          } else {
            const deliveryData = await fetchDeliveriesForCustomer();
            if (deliveryData && deliveryData.length > 0) {
              const delivery = deliveryData[0];
              setFormData(prevFormData => ({
                ...prevFormData,
                scheduled_delivery_date: delivery.scheduled_delivery_date ? new Date(delivery.scheduled_delivery_date).toISOString().split('T')[0] : '',
                client_id: delivery.client_id,
                delivery_id: delivery.delivery_id
              }));
              setSelectedClient({ client_id: delivery.client_id, name: delivery.client_name });
            }
          }
        } catch (error) {
          console.error('Error fetching deliveries for customer:', error);
          setShowError(true);
          setErrorMessage('Failed to load deliveries for customer');
        } finally {
          setIsLoading(false);
        }
      }
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    fetchOrderData();
  }, [orderId]);

  useEffect(() => {
    if (formData.menu_id && formData.menu_id !== selectedMenu.menu_id) {
      setSelectedMenu({ menu_id: formData.menu_id, name: '' });
    }
  }, [formData.menu_id]);

  const [isAddingMenuItems, setIsAddingMenuItems] = useState(false);
  const [autoAdded, setAutoAdded] = useState(false);
  useEffect(() => {
    if (
      orderId &&
      !autoAdded &&
      selectedCustomer.customer_id &&
      formData.menu_id &&
      (formData.items.length === 0 ||
      (formData.items.length === 1 && formData.items[0].item_id === null))
    ) {
      addAllItemsFromMenuSelected(formData.menu_id);
      setAutoAdded(true);
    }
  }, [orderId, autoAdded, selectedCustomer.customer_id, formData.client_id, formData.menu_id, formData.items]);

  const fetchOrderDetails = async () => {
    if (!orderId) return;

    setIsLoading(true);
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await axios.get(`${apiUrl}/orders/${orderId}`, { headers: getHeaders(apiToken) });
      const orderData = response.data.data[0];
      setFormData({
        ...formData,
        ...orderData,
        delivery_date: orderData.delivery_date ? new Date(orderData.delivery_date).toISOString().split('T')[0] : '',
        scheduled_delivery_date: orderData.scheduled_delivery_date ? new Date(orderData.scheduled_delivery_date).toISOString().split('T')[0] : '',
      });
      setShowError(false);
    } catch (error) {
      console.error('Error fetching order data:', error);
      setShowError(true);
      setErrorMessage('Failed to load order data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    if (formData.delivery_id !== null) {
      navigate(`/orders?delivery_id=${formData.delivery_id}`);
    } else {
      navigate('/orders');
    }
  };

  const handleBackToCustomer = () => {
    navigate(`/clients/${selectedCustomer.customer_id}`);
  };

  const handleBackToDelivery = () => {
    navigate(`/deliveries/${formData.delivery_id}`);
  };

  const handleModalOpen = (modalType, isOpen) => {
    if (modalType === 'customer') {
      setIsCustomerModalOpen(isOpen);
    } else if (modalType === 'client') {
      setIsClientModalOpen(isOpen);
    }
  };

  const handleSelectClient = (client) => {
    setFormData({ ...formData, client_id: client.client_id });
    setSelectedClient(client);
  };

  const handleSelectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_id: customer.customer_id,
      meal_count: customer.meal_count,
      menu_id: customer.default_menu_id
    });
    setSelectedCustomer(customer);
    setSelectedMenu({ menu_id: customer.default_menu_id, name: '' });
  };

  const handleSelectDelivery = (delivery) => {
    setFormData({
      ...formData,
      scheduled_delivery_date: new Date(delivery.scheduled_delivery_date).toISOString().split('T')[0],
      delivery_id: delivery.delivery_id,
      client_id: delivery.client_id
    });
  };

  const handleMenuSelect = (menu) => {
    if (isEditable && formData.items.length > 0) {
      if (!window.confirm("Are you sure you would like to change the menu selection? This will clear all order items for this order.")) {
        return;
      }
    }
    setSelectedMenu(menu);
    setFormData(prevState => ({ ...prevState, default_menu_id: menu.menu_id }));
    if (isEditable) {
      setFormData(prevState => ({ ...prevState, items: [] }));
      addAllItemsFromMenuSelected(menu.menu_id);
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    const newValue = value instanceof Object ? value.item_id : value;
    newItems[index][field] = field === 'quantity' ? Math.max(parseInt(value) || 0, 0) : newValue;
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items, 
        { order_item_id: null, item_id: null, variation_id: null, quantity: 1, create_date: null, modified_date: null, sort_order: null }
      ]
    });
  };

  const removeItem = (index) => {
    const newItems = [...formData.items];
    newItems.splice(index, 1);
    setFormData({ ...formData, items: newItems });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const addAllItemsFromMenuSelected = async (menuIdParam) => {
    const menuId = menuIdParam || selectedMenu.menu_id;
    if (!selectedCustomer.customer_id || !menuId) return;
    setIsAddingMenuItems(true);
    setIsLoading(true);
    try {
      const apiToken = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_API_URL;
      const menuResponse = await axios.get(`${apiUrl}/menu_items/${menuId}`, {
        headers: getHeaders(apiToken),
      });
      const selectedMenuItems = menuResponse.data.data;
      if (selectedMenuItems.length) {
        const sortedItems = [...selectedMenuItems].sort((a, b) => a.sort_order - b.sort_order);
        const formattedItems = sortedItems.map(item => ({
          item_id: item.item_id,
          sort_order: item.sort_order || null,
          quantity: 0,
        }));
        const updatedOrder = {
          ...formData,
          items: formattedItems,
          menu_id: menuId,
        };
        const updatedOrderWithId = {
            ...updatedOrder,
            order_id: orderId,
        };
        await saveOrder(updatedOrderWithId);
        setFormData(updatedOrder);
        setShowSuccess(true);
        setSuccessMessage('Menu items added and order updated successfully');
        setErrorMessage('');
        setShowError(false);
        if (!formData.order_id && orderId) {
          navigate(`/orders/${orderId}`);
        }
      } else {
        setShowError(true);
        setErrorMessage('No items found in the selected menu.');
      }
    } catch (error) {
      console.error('Error adding menu items:', error);
      setShowError(true);
      setErrorMessage('Failed to add menu items and update order');
    } finally {
      setIsAddingMenuItems(false);
      setIsLoading(false);
    }
  };

  // Updated addItemsFromDefaultOrder to accept a defaultOrderNumber parameter.
  const addItemsFromDefaultOrder = async (defaultOrderNumber) => {
    const customerId = selectedCustomer.customer_id || formData.customer_id;
    if (!customerId) return;
    
    setIsLoading(true);
    try {
      const defaultOrderItems = await fetchItemsFromDefaultOrder(customerId, defaultOrderNumber);
      if (defaultOrderItems.length) {
        setFormData(prevState => ({
          ...prevState,
          items: defaultOrderItems
        }));
        setShowSuccess(true);
        setSuccessMessage(`Added items from default order ${defaultOrderNumber}`);
        setErrorMessage('');
        setShowError(false);
      } else {
        setShowError(true);
        setErrorMessage(`No default order ${defaultOrderNumber} items found for customer.`);
      }
    } catch (error) {
      console.error('Error fetching default order items:', error);
      setShowError(true);
      setErrorMessage('Failed to add items from default order');
    } finally {
      setIsLoading(false);
    }
  };

  // New state and handler for the DefaultOrderModal.
  const [showDefaultOrderModal, setShowDefaultOrderModal] = useState(false);
  const handleSelectDefaultOrderNumber = (orderNumber) => {
    setShowDefaultOrderModal(false);
    addItemsFromDefaultOrder(orderNumber);
  };

  const handleSubmit = async (e) => {
    setShowError(false);
    e.preventDefault();
    if (isCustomerModalOpen || isClientModalOpen) return;
    if (!isDefaultOrder) {
      const outOfStockItems = formData.items.filter(
        (item) => Number(item.quantity) > 0 && item.stock_status && item.stock_status !== 'in_stock'
      );
      if (outOfStockItems.length > 0) {
        setShowError(true);
        setErrorMessage('Some items in the order are not in stock. Please remove from the order in order to continue.');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }
    setIsLoading(true);
    try {
      const orderId = await saveOrder(formData);
      fetchOrderDetails();
      if (orderId && !formData.order_id) {
        navigate(`/orders/${orderId}`);
      }
      setShowSuccess(true);
      setSuccessMessage('Order and items created/updated successfully');
      setErrorMessage('');
      setShowError(false);
    } catch (error) {
      setShowError(true);
      setErrorMessage('Failed to submit order');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintOrder = async () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
      const apiToken = localStorage.getItem('accessToken');
      const url = orderId ? `${apiUrl}/orders/${orderId}/pdf` : '';
      const response = await axios.get(url, {
        responseType: 'blob',
        headers: getHeaders(apiToken)
      });
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      handlePdfFile(pdfBlob, `order_${orderId}.pdf`, 'print');
    } catch (error) {
      setShowError(true);
      setErrorMessage('Failed to print PDF');
      console.error('Error generating PDF:', error);
    }
  };

  const pageTitle = `${orderId ? 'Edit' : 'Create'} ${isDefaultOrder ? 'Default ' : ''}Order`;
  const shouldShowAddItemsButton = !isDefaultOrder &&
    selectedCustomer.customer_id &&
    formData.items.every(item => item.item_id === null);

  return (
    <MasterLayout pageTitle={pageTitle}>
      {showError && <ErrorMessage message={errorMessage} />}
      {showSuccess && <SuccessMessage message={successMessage} />}
      {isAddingMenuItems && <InfoMessage message="Adding menu items to order..." />}
      {isLoading ? (
        <Spinner />
      ) : (
        <form onSubmit={handleSubmit} className="p-4">
          <DefaultOrderModal
            isOpen={showDefaultOrderModal}
            onClose={() => setShowDefaultOrderModal(false)}
            onSelect={handleSelectDefaultOrderNumber}
          />
          <div className="flex flex-nowrap gap-6 mb-6">
            {!isDefaultOrder && (
              <>
                <div className="flex-1">
                  <label htmlFor="scheduled_delivery_date" className="block mb-2 text-sm font-medium text-gray-900">Scheduled Delivery Date</label>
                  <input
                    type="date"
                    id="scheduled_delivery_date"
                    name="scheduled_delivery_date"
                    value={formData.scheduled_delivery_date}
                    readOnly
                    disabled
                    className="bg-gray-200 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                  />
                </div>
                {!isCustomer && formData.delivery_date && (
                  <div className="flex-1">
                    <label htmlFor="delivery_date" className="block mb-2 text-sm font-medium text-gray-900">
                      Delivery Date
                    </label>
                    <input
                      type="date"
                      id="delivery_date"
                      name="delivery_date"
                      value={formData.delivery_date}
                      readOnly
                      disabled
                      className="bg-gray-200 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                    />
                  </div>
                )}
                {!isCustomer && (
                  <div className="flex-1">
                    <label htmlFor="delivery_dropdown" className="block mb-2 text-sm font-medium text-gray-900">Select Delivery</label>
                    <ClientDeliveryDropdown clientId={formData.client_id} onSelect={handleSelectDelivery} />
                  </div>
                )}
              </>
            )}
            {!isDefaultOrder && !isCustomer && (
              <ClientModal isOpen={isClientModalOpen} setIsOpen={(isOpen) => handleModalOpen('client', isOpen)} selectClient={handleSelectClient} value={selectedClient} isSelectable={false} />
            )}
          </div>

          <div className="grid gap-6 mb-6 md:grid-cols-3">
            <div className={shouldShowAddItemsButton ? "flex items-end" : ""}>
              {!isCustomer && (
                <CustomerModal isOpen={isCustomerModalOpen} setIsOpen={(isOpen) => handleModalOpen('customer', isOpen)} selectCustomer={handleSelectCustomer} value={selectedCustomer} client={selectedClient} disabled={isDefaultOrder} />
              )}
            </div>
            <MenuDropdown
              onSelect={handleMenuSelect}
              selectedMenu={selectedMenu}
            />
            <SimpleNumberInput
              label="Number of Meals"
              value={formData.meal_count}
              onChange={(newValue) => handleChange({ target: { name: 'meal_count', value: newValue } })}
              min={1}
              disabled={!isEditable || (selectedCustomer && selectedCustomer.meal_count != null)}
            />
          </div>
          { items.length === 0 && <Spinner /> }
          { !isCustomer && selectedCustomer.customer_id && items.length > 0 && (
            <div className="mb-6 lg:grid lg:grid-cols-1 lg:gap-6">
              <OrderItems
                orderId={orderId}
                items={items}
                orderItems={formData.items}
                handleItemChange={handleItemChange}
                addItem={addItem}
                removeItem={removeItem}
                maxItems={formData.meal_count}
                disabled={!isEditable}
                quantityRefs={quantityRefs}
                onEnterPress={handleEnterPress}
                refreshOrderItems={fetchOrderData}
                disableAddItems={true}
                onSuccess={(message) => {
                  setSuccessMessage(message);
                  setShowSuccess(true);
                  setShowError(false);
                  setErrorMessage('');
                }}
                onError={(message) => {
                  setErrorMessage(message);
                  setShowError(true);
                  setShowSuccess(false);
                }}
                clearInsteadOfDelete={isEditable ? !!selectedMenu.menu_id : false}
              />
            </div>
          )}
          {shouldShowAddItemsButton && (
            <div className="flex justify-center mt-6">
              <GeneralButton
                type="button"
                buttonType="secondary"
                onClick={() => setShowDefaultOrderModal(true)}
              >
                Add Default Order Items
              </GeneralButton>
            </div>
          )}
          <div className="flex justify-center mt-6">
            <GeneralButton type="submit" buttonType="primary" disabled={!isEditable}>
              {orderId ? 'Update Order' : 'Create Order'}
            </GeneralButton>
            {!isEditable && !isCustomer && selectedCustomer.customer_id && (
              <GeneralButton 
                type="button" 
                buttonType="secondary" 
                onClick={addAllItemsFromMenuSelected}
              >
                Add All Menu Items
              </GeneralButton>
            )}
            {formData.delivery_id && (
              <GeneralButton type="button" buttonType="secondary" onClick={handleBack}>
                View Order Delivery List
              </GeneralButton>
            )}
            {formData.delivery_id && !isCustomer && (
              <GeneralButton type="button" buttonType="secondary" onClick={handleBackToDelivery}>
                View Delivery for Order
              </GeneralButton>
            )}
            {!isCustomer && selectedCustomer.customer_id && (
              <GeneralButton 
                type="button" 
                buttonType="secondary" 
                onClick={handleBackToCustomer}
              >
                View Customer for Order
              </GeneralButton>
            )}
          </div>
          {!isCustomer && (<div className="flex justify-center mt-6">
            <GeneralButton type="button" buttonType="primary" onClick={handlePrintOrder}>
              Print Order
            </GeneralButton>
          </div>
          )}
        </form>
      )}
    </MasterLayout>
  );
}

async function saveOrder(orderData) {
  const apiToken = localStorage.getItem('accessToken');
  const apiUrl = process.env.REACT_APP_API_URL;
  const normalizedItems = orderData.items.map(item => ({
    ...item,
    variation_id: item.variation_id === '' ? null : item.variation_id,
    quantity: item.quantity || 0,
    price: item.price || 0,
    item_cost: item.item_cost || 0,
    sort_order: item.sort_order || null,
    stock_status: item.stock_status || 'in_stock',
  }));
  const normalizedFormData = { ...orderData, items: normalizedItems };
  const method = normalizedFormData.order_id ? 'put' : 'post';
  const orderEndpoint = `${apiUrl}/orders${normalizedFormData.order_id ? `/${normalizedFormData.order_id}` : ''}`;
  const orderResponse = await axios({
    method,
    url: orderEndpoint,
    data: normalizedFormData,
    headers: getHeaders(apiToken),
  });
  if (orderResponse.status === 201 || orderResponse.status === 200) {
    const rawOrderId = orderResponse.data.data?.order_id;
    const orderId =
      typeof rawOrderId === 'object' && rawOrderId.order_id
      ? rawOrderId.order_id
      : rawOrderId || normalizedFormData.order_id;
    if (method === 'put') {
      const existingResponse = await axios.get(`${apiUrl}/orders/${orderId}/items`, { headers: getHeaders(apiToken) });
      const existingItems = existingResponse.data.data;
      const normalizedItemIds = normalizedItems
        .filter(item => item.order_item_id)
        .map(item => item.order_item_id);
      const deletePromises = existingItems
        .filter(item => !normalizedItemIds.includes(item.order_item_id))
        .map(item =>
          axios.delete(`${apiUrl}/order-items/${item.order_item_id}`, { headers: getHeaders(apiToken) })
        );
      await Promise.all(deletePromises);
    }
    const itemPromises = normalizedItems.map(item => {
      const itemMethod = item.order_item_id ? 'put' : 'post';
      const itemEndpoint = item.order_item_id
        ? `${apiUrl}/order-items/${item.order_item_id}`
        : `${apiUrl}/orders/${orderId}/items`;
      return axios({
        method: itemMethod,
        url: itemEndpoint,
        data: { ...item, order_id: orderId },
        headers: getHeaders(apiToken),
      });
    });
    await Promise.all(itemPromises);
    return orderId;
  }
}

export default OrderForm;
